import React, { useEffect, useMemo } from 'react'
import ReactDOM from 'react-dom'
import styles from './LaunchGrid.module.scss'
import CloseIcon from '@mui/icons-material/Close'

// A full-screen launcher: the same actions as a SpeedDial, but as a grid of
// compact tiles you pick from. Easier to scan and to hit on a touch screen than
// a fan of small circles.
//
//   <LaunchGrid title="Learn" actions={learnActions} onClose={…} />
//
// Actions may carry a `group` label; tiles are then shown under that heading, in
// the order the groups first appear. Actions without one fall into a single
// unlabelled section, so the component still works with a plain list.
//
// The tile sections are also exported on their own (`ActionTiles`) so the home
// page can lay the same registry out inline. Their colours read the theme
// tokens of whatever they sit in and fall back to the dark palette.
//
// Portalled to <body> on purpose: the HUD's button row is `transform: skew(…)`,
// which creates a containing block — a fixed overlay rendered inside it would be
// clipped and skewed with the row.

// One tint per group, so the sections are scannable at a glance.
const TINTS = [
  { fg: '#34d399', bg: 'rgba(52,211,153,0.12)', br: 'rgba(52,211,153,0.32)' },
  { fg: '#60a5fa', bg: 'rgba(96,165,250,0.12)', br: 'rgba(96,165,250,0.32)' },
  { fg: '#fbbf24', bg: 'rgba(251,191,36,0.12)', br: 'rgba(251,191,36,0.32)' },
  { fg: '#a78bfa', bg: 'rgba(167,139,250,0.12)', br: 'rgba(167,139,250,0.32)' },
]

// Group, preserving first-seen order.
const groupActions = (actions) => {
  const order = []
  const map = new Map()
  actions.forEach((a) => {
    const g = a.group || ''
    if (!map.has(g)) { map.set(g, []); order.push(g) }
    map.get(g).push(a)
  })
  return order.map((name) => ({ name, items: map.get(name) }))
}

// The grouped tile sections. `onPick(action)` is called with the tile's action;
// `animate` staggers the tiles in (off for the inline home-page use).
export function ActionTiles({ actions = [], onPick, animate = true }) {
  const groups = useMemo(() => groupActions(actions), [actions])
  let n = -1 // running index, so the stagger flows across every group
  return groups.map((gr, gi) => {
    const t = TINTS[gi % TINTS.length]
    return (
      <section key={gr.name || '_ungrouped'} className={styles.group}
        style={{ '--tint': t.fg, '--tint-bg': t.bg, '--tint-br': t.br }}>
        {gr.name ? <div className={styles.groupLabel}>{gr.name}</div> : null}
        <div className={styles.grid}>
          {gr.items.map((a) => {
            n += 1
            return (
              <button key={a.id || a.name} type="button" className={`${styles.tile} ${animate ? styles.tileRise : ''}`}
                style={animate ? { animationDelay: `${Math.min(n * 22, 300)}ms` } : undefined}
                onClick={() => onPick && onPick(a)}>
                <span className={styles.tileIcon}>{a.icon}</span>
                <span className={styles.tileName}>{a.name}</span>
              </button>
            )
          })}
        </div>
      </section>
    )
  })
}

export default function LaunchGrid({ title = 'Menu', subtitle = '', actions = [], onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose && onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const pick = (action) => {
    if (onClose) onClose()          // close first, so the chosen view lands on top
    if (action && action.onClick) action.onClick()
  }

  return ReactDOM.createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.head}>
          <div>
            <div className={styles.label}>Choose one</div>
            <h1 className={styles.title}>{title}</h1>
            {subtitle ? <p className={styles.sub}>{subtitle}</p> : null}
          </div>
          <button className={styles.closeBtn} onClick={onClose} title="Close (Esc)"><CloseIcon /></button>
        </div>
        <ActionTiles actions={actions} onPick={pick} />
      </div>
    </div>,
    document.body
  )
}
